/**
 * WHAT YOU HAVE TO PUT ON THE TRUCK, AND WHY THAT NUMBER.
 *
 * Derived from the sections and nothing else. Feet come from what somebody
 * measured or estimated, never from pixels: a photograph has no scale, and a
 * pixel length is not a billable foot.
 *
 * THIS IS NOT PRICING. The seasonal ladder, the rate table, the discount
 * precedence and the quote document are lib/pricing.ts and lib/quote.ts, all
 * of them working and none of them touched here. This file answers a
 * different question - how many bulbs, how many clips, how much wire - which
 * is the question a warehouse asks and a quote never quite answers.
 *
 * EVERY LINE CARRIES ITS OWN ARITHMETIC. Not a tooltip and not documentation:
 * the string that explains the number is produced by the same function that
 * produced the number, so the two cannot drift. That is REQ-06-07, and it is
 * the difference between a contractor trusting a takeoff and re-counting it by
 * hand on the tailgate.
 *
 * AND EVERY LINE SAYS HOW MUCH OF IT RESTS ON A GUESS. A count derived from a
 * length nobody has walked is reported with that length beside it, because a
 * bill of materials that reads as one confident number is exactly how a truck
 * arrives four hundred bulbs short.
 */

import { isVerified, type RoofSection } from './sections'

/**
 * The allowance on wire. Five percent, from the v0.2 fixture, and it is a
 * fixture rather than a recommendation: real allowance depends on how the
 * drops are run and belongs in the account's settings when that screen exists.
 */
export const WIRE_ALLOWANCE = 1.05

export interface MaterialLine {
  key: 'bulbs' | 'clips' | 'wire' | 'footage'
  label: string
  qty: number
  unit: string
  /** The arithmetic, in words, produced with the number. */
  how: string
  /** How much of this quantity rests on a length nobody has walked. */
  fromUnverified: number
}

export interface Takeoff {
  lines: MaterialLine[]
  /** Feet across included sections. */
  feet: number
  /** Of those feet, how many nobody has measured at the house. */
  unverifiedFeet: number
  /** Included sections with no length at all. Their materials cannot exist. */
  missing: number
  /** True when every included section has been measured at the house. */
  solid: boolean
}

/**
 * BULBS ARE PER RUN, NOT PER JOB.
 *
 * A twelve foot run at twelve inch spacing is thirteen bulbs, not twelve: both
 * ends carry one. Summing the feet first and adding a single bulb at the end
 * loses one bulb per run, which on a house with seven sections is six bulbs
 * short and a second trip to the van.
 */
export const bulbsForFeet = (feet: number, spacingInches: number): number => {
  if (feet <= 0 || spacingInches <= 0) return 0
  return Math.ceil((feet * 12) / spacingInches) + 1
}

/**
 * The takeoff for one job.
 *
 * Excluded sections contribute nothing anywhere - not to footage, not to
 * bulbs, not to the allowance - because a section somebody deliberately left
 * out is not quiet revenue waiting to be found.
 */
export function takeoff(sections: RoofSection[], spacingInches: number): Takeoff {
  const included = sections.filter((s) => !s.excluded)

  let feet = 0
  let unverifiedFeet = 0
  let bulbs = 0
  let unverifiedBulbs = 0
  let missing = 0

  for (const s of included) {
    const f = s.measurement.feet
    if (f <= 0) {
      missing++
      continue
    }
    const b = bulbsForFeet(f, spacingInches)
    feet += f
    bulbs += b
    if (!isVerified(s.measurement)) {
      unverifiedFeet += f
      unverifiedBulbs += b
    }
  }

  const round = (n: number) => Math.round(n * 100) / 100
  feet = round(feet)
  unverifiedFeet = round(unverifiedFeet)

  const wire = Math.ceil(feet * WIRE_ALLOWANCE)
  const unverifiedWire = Math.ceil(unverifiedFeet * WIRE_ALLOWANCE)
  const runs = included.filter((s) => s.measurement.feet > 0).length
  const inches = spacingInches

  const lines: MaterialLine[] = [
    {
      key: 'footage',
      label: 'Installed footage',
      qty: feet,
      unit: 'ft',
      how:
        runs === 0
          ? 'Nothing with a length on it yet.'
          : `The lengths on ${runs} ${runs === 1 ? 'section' : 'sections'}, added up.`,
      fromUnverified: unverifiedFeet,
    },
    {
      key: 'bulbs',
      label: 'Bulbs',
      qty: bulbs,
      unit: '',
      how:
        runs === 0
          ? 'No lengths, so no count.'
          : `Feet times 12, divided by ${inches} inch spacing, rounded up, plus one per section for the far end. Counted per section, not on the total.`,
      fromUnverified: unverifiedBulbs,
    },
    {
      key: 'clips',
      label: 'Clips',
      qty: bulbs,
      unit: '',
      // Deliberately the same number rather than a second calculation. If the
      // rule ever stops being one clip per bulb it changes in one place.
      how: 'One per bulb.',
      fromUnverified: unverifiedBulbs,
    },
    {
      key: 'wire',
      label: 'Wire',
      qty: wire,
      unit: 'ft',
      how: `Installed footage plus ${Math.round((WIRE_ALLOWANCE - 1) * 100)} percent, rounded up. Drops to the outlet are not in this.`,
      fromUnverified: unverifiedWire,
    },
  ]

  return {
    lines,
    feet,
    unverifiedFeet,
    missing,
    solid: included.length > 0 && missing === 0 && unverifiedFeet === 0,
  }
}

/**
 * WHAT A PERSON IS ALLOWED TO CONCLUDE FROM THIS LIST.
 *
 * Never "order this". A takeoff built on a length nobody has walked is a
 * shopping list with a guess in it, and the sentence says which.
 */
export function takeoffWording(t: Takeoff): string {
  if (t.missing > 0) {
    return t.missing === 1
      ? 'One section has no length, so it is missing from every number here.'
      : `${t.missing} sections have no length, so they are missing from every number here.`
  }
  if (t.feet <= 0) return 'Nothing to count yet.'
  if (t.unverifiedFeet > 0) {
    return `${Math.round(t.unverifiedFeet)} of these ${Math.round(t.feet)} feet have not been measured at the house.`
  }
  return 'Every foot in this list was measured at the house.'
}
